import React, { useState } from 'react';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { uploadFile } from '../../../utils/storageUtils';
import { compressImage } from '../../../utils/imageUtils';

const RepairCompletionModal = ({ 
  isOpen, 
  onClose, 
  repair, 
  equipmentData,
  onSuccess 
}) => {
  const { requestRepairCompletion } = useEquipment();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  
  const [completionReason, setCompletionReason] = useState('');
  const [completionCondition, setCompletionCondition] = useState('GOOD');
  const [completionConditionPercentage, setCompletionConditionPercentage] = useState(80);
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionImages, setCompletionImages] = useState([]);
  const [completionCost, setCompletionCost] = useState('');
  const [uploadingCompletionImages, setUploadingCompletionImages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !repair) return null;

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check plan-based limit
    const userPlan = currentUser?.plan || 'FREE';
    const maxImages = userPlan === 'PRO' ? 3 : 1;
    const remainingSlots = maxImages - completionImages.length;

    if (remainingSlots <= 0) {
      alert(`ถึงขีดจำกัดการอัพโหลดรูปภาพแล้ว (${maxImages} รูป${userPlan === 'FREE' ? ' - สมัคร Pro Plan เพื่ออัพโหลดได้สูงสุด 3 รูป' : ''})`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      alert(`สามารถอัพโหลดได้อีก ${remainingSlots} รูปเท่านั้น (สูงสุด ${maxImages} รูป)`);
    }

    try {
      setUploadingCompletionImages(true);
      const uploadedUrls = [];

      for (const file of filesToUpload) {
        const compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.8
        });

        const fileName = `equipment-repair-completion-${Date.now()}-${Math.random().toString(36).substring(7)}`;
        const path = `equipment/${currentTeam.id}/${fileName}`;
        const url = await uploadFile(compressedFile, path);
        uploadedUrls.push(url);
      }

      setCompletionImages([...completionImages, ...uploadedUrls]);
    } catch (err) {
      console.error('Error uploading images:', err);
      alert('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ');
    } finally {
      setUploadingCompletionImages(false);
    }
  };

  const handleSubmit = async () => {
    if (!completionReason.trim()) {
      alert('กรุณาระบุรายละเอียดการซ่อม');
      return;
    }

    // Validate percentage matches condition
    const conditionRanges = {
      'EXCELLENT': [90, 100],
      'GOOD': [70, 89],
      'FAIR': [50, 69],
      'POOR': [0, 49]
    };
    const [min, max] = conditionRanges[completionCondition] || [0, 100];
    if (completionConditionPercentage < min || completionConditionPercentage > max) {
      alert(`เปอร์เซ็นต์สภาพต้องอยู่ระหว่าง ${min}-${max}% สำหรับสภาพ "${completionCondition}"`);
      return;
    }

    try {
      setIsSubmitting(true);
      const completionData = {
        reason: completionReason.trim(),
        condition: completionCondition,
        conditionPercentage: completionConditionPercentage,
        notes: completionNotes.trim(),
        images: completionImages,
        cost: completionCost ? parseFloat(completionCost) : null
      };
      const result = await requestRepairCompletion(repair.id, completionData);
      if (result.success) {
        alert('ส่งรายงานผลการซ่อมสำเร็จ รอการอนุมัติจากหัวหน้าทีม');
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error requesting completion:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCompletionReason('');
    setCompletionCondition('GOOD');
    setCompletionConditionPercentage(80);
    setCompletionNotes('');
    setCompletionImages([]);
    setCompletionCost('');
    onClose();
  };

  const equipmentName = repair.equipmentName || equipmentData?.name;
  const equipmentCode = repair.equipmentCode || equipmentData?.code;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-800 mb-2">รายงานผลการซ่อม</h3>
        <p className="text-sm text-gray-600 mb-4">กรอกข้อมูลการซ่อมที่เสร็จแล้ว เพื่อส่งให้หัวหน้าทีมอนุมัติ</p>
        
        {/* Equipment Info */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🩺</span>
            <div>
              <h4 className="font-semibold text-gray-800">{equipmentName}</h4>
              <p className="text-sm text-gray-600">{equipmentCode}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Completion Condition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              สภาพอุปกรณ์หลังซ่อม <span className="text-red-500">*</span>
            </label>
            <select
              value={completionCondition}
              onChange={(e) => {
                setCompletionCondition(e.target.value);
                // Auto-set percentage based on condition
                const percentageMap = {
                  'EXCELLENT': 95,
                  'GOOD': 80,
                  'FAIR': 60,
                  'POOR': 30
                };
                setCompletionConditionPercentage(percentageMap[e.target.value] || 80);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="EXCELLENT">ดีมาก (90-100%)</option>
              <option value="GOOD">ดี (70-89%)</option>
              <option value="FAIR">พอใช้ (50-69%)</option>
              <option value="POOR">ยังมีปัญหา (0-49%)</option>
            </select>
          </div>

          {/* Completion Condition Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เปอร์เซ็นต์สภาพ <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="100"
                value={completionConditionPercentage}
                onChange={(e) => setCompletionConditionPercentage(parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="text-lg font-semibold text-gray-800 w-16 text-right">
                {completionConditionPercentage}%
              </span>
            </div>
            <div className="mt-2 flex justify-between text-xs text-gray-500">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Completion Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              หมายเหตุเพิ่มเติม
            </label>
            <textarea
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              rows={3}
              placeholder="ระบุหมายเหตุเพิ่มเติมเกี่ยวกับสภาพอุปกรณ์..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Completion Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              รูปภาพสภาพหลังซ่อม
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              disabled={uploadingCompletionImages}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {uploadingCompletionImages && (
              <p className="text-sm text-blue-600 mt-2">กำลังอัปโหลด...</p>
            )}
            {completionImages.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {completionImages.map((url, index) => (
                  <div key={index} className="relative">
                    <img
                      src={url}
                      alt={`Completion ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setCompletionImages(completionImages.filter((_, i) => i !== index))}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Completion Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ค่าใช้จ่ายในการซ่อม (บาท)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={completionCost}
              onChange={(e) => setCompletionCost(e.target.value)}
              placeholder="ระบุค่าใช้จ่าย (ถ้ามี)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Completion Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              รายละเอียดการซ่อม <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              อธิบายว่าซ่อมอะไรไปบ้าง หรือสรุปการซ่อมที่ทำ
            </p>
            <textarea
              value={completionReason}
              onChange={(e) => setCompletionReason(e.target.value)}
              rows={4}
              placeholder="เช่น: เปลี่ยนแบตเตอรี่, ซ่อมปั๊ม, เปลี่ยนสายไฟ..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            ยกเลิก
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !completionReason.trim() || uploadingCompletionImages}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {isSubmitting ? 'กำลังส่ง...' : 'ยืนยันการซ่อมเสร็จ'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepairCompletionModal;

